import { useEffect, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import {
  adminAnalytics,
  type SalesDataPoint,
  type TopProduct,
  type BrandSalesData,
  type CustomerMetric,
  type OrderMetric,
  type SalesSummary,
} from '../../api/admin';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  AreaChart, Area,
} from 'recharts';
import {
  BarChart3, TrendingUp, Users, ShoppingBag,
  CalendarDays, UserCheck, UserPlus, RotateCcw,
  Package, Repeat,
} from 'lucide-react';

const BRAND_COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899'];

type Granularity = 'daily' | 'weekly' | 'monthly';
type Period = 'day' | 'week' | 'month' | 'year' | 'all';

export default function AnalyticsPage() {
  const { isDark } = useTheme();
  const [granularity, setGranularity] = useState<Granularity>('daily');
  const [period, setPeriod] = useState<Period>('month');
  const [salesChart, setSalesChart] = useState<SalesDataPoint[]>([]);
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [brandSales, setBrandSales] = useState<BrandSalesData[]>([]);
  const [customerMetrics, setCustomerMetrics] = useState<CustomerMetric | null>(null);
  const [orderMetrics, setOrderMetrics] = useState<OrderMetric | null>(null);
  const [salesSummary, setSalesSummary] = useState<SalesSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      adminAnalytics.salesChart(granularity).catch(() => []),
      adminAnalytics.topProducts(period === 'day' ? 'today' : period, 10).catch(() => []),
      adminAnalytics.brandSales(period === 'day' ? 'today' : period).catch(() => []),
      adminAnalytics.customerMetrics(period === 'day' ? 'today' : period).catch(() => null),
      adminAnalytics.orderMetrics(period === 'day' ? 'today' : period).catch(() => null),
      adminAnalytics.salesSummary(period === 'day' ? 'today' : period).catch(() => null),
    ]).then(([chart, tp, bs, cm, om, ss]) => {
      setSalesChart(chart);
      setTopProducts(tp);
      setBrandSales(bs);
      setCustomerMetrics(cm);
      setOrderMetrics(om);
      setSalesSummary(ss);
    }).catch(console.error).finally(() => setLoading(false));
  }, [granularity, period]);

  const fmt = (n: number) => `₹${n.toLocaleString('en-IN')}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><BarChart3 size={24} /> Analytics</h1>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Deep dive into your store metrics</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={period}
            onChange={e => setPeriod(e.target.value as Period)}
            className={`px-3 py-1.5 rounded-lg border text-sm outline-none
              ${isDark ? 'bg-gray-900 border-gray-800 text-white' : 'bg-white border-gray-200'}`}
          >
            <option value="day">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
            <option value="all">All Time</option>
          </select>
        </div>
      </div>

      {/* Sales summary cards */}
      {salesSummary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className={`rounded-xl border p-5 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={16} className="text-emerald-500" />
              <span className={`text-xs uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Revenue</span>
            </div>
            <p className="text-2xl font-bold">{fmt(salesSummary.total_revenue)}</p>
          </div>
          <div className={`rounded-xl border p-5 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-2 mb-1">
              <ShoppingBag size={16} className="text-blue-500" />
              <span className={`text-xs uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Orders</span>
            </div>
            <p className="text-2xl font-bold">{salesSummary.total_orders}</p>
          </div>
          <div className={`rounded-xl border p-5 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-2 mb-1">
              <CalendarDays size={16} className="text-purple-500" />
              <span className={`text-xs uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Avg Order</span>
            </div>
            <p className="text-2xl font-bold">{fmt(salesSummary.avg_order_value)}</p>
          </div>
        </div>
      )}

      {/* Revenue chart */}
      <div className={`rounded-xl border p-5 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="font-semibold flex items-center gap-2">
            <TrendingUp size={18} className="text-emerald-500" />
            Revenue Over Time
          </h2>
          <div className="flex rounded-lg overflow-hidden border text-xs">
            {(['daily', 'weekly', 'monthly'] as Granularity[]).map(g => (
              <button
                key={g}
                onClick={() => setGranularity(g)}
                className={`px-3 py-1.5 transition-colors
                  ${granularity === g
                    ? 'bg-emerald-500 text-white'
                    : isDark ? 'bg-gray-800 text-gray-400 hover:text-white border-gray-700' : 'bg-gray-50 text-gray-500 hover:text-gray-700 border-gray-200'
                  }`}
              >
                {g.charAt(0).toUpperCase() + g.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={salesChart}>
              <defs>
                <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1f2937' : '#e5e7eb'} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: isDark ? '#6b7280' : '#9ca3af' }} />
              <YAxis tick={{ fontSize: 10, fill: isDark ? '#6b7280' : '#9ca3af' }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{
                  backgroundColor: isDark ? '#111827' : '#fff',
                  border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                  borderRadius: '8px', fontSize: 12,
                }}
                formatter={(value) => [`₹${Number(value).toLocaleString('en-IN')}`, 'Revenue']}
              />
              <Area type="monotone" dataKey="revenue" stroke="#10b981" fillOpacity={1} fill="url(#areaGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts row: Brand pie + top products */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Brand pie chart */}
        <div className={`rounded-xl border p-5 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
          <h2 className="font-semibold mb-4">Brand Distribution</h2>
          {brandSales.length === 0 ? (
            <p className={`text-sm text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No data yet</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={brandSales}
                    dataKey="revenue"
                    nameKey="brand"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                    labelLine={false}
                    fontSize={10}
                  >
                    {brandSales.map((_, i) => (
                      <Cell key={i} fill={BRAND_COLORS[i % BRAND_COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Tooltip formatter={(value) => [fmt(Number(value)), 'Revenue']} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Top products */}
        <div className={`rounded-xl border p-5 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
          <h2 className="font-semibold mb-4">Top Products</h2>
          {topProducts.length === 0 ? (
            <p className={`text-sm text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No data yet</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topProducts.slice(0, 7)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1f2937' : '#e5e7eb'} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: isDark ? '#6b7280' : '#9ca3af' }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
                  <YAxis
                    type="category"
                    dataKey="product_name"
                    width={120}
                    tick={{ fontSize: 9, fill: isDark ? '#9ca3af' : '#6b7280' }}
                    tickFormatter={(v: string) => v.length > 18 ? v.slice(0, 18) + '...' : v}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: isDark ? '#111827' : '#fff', border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`, borderRadius: '8px', fontSize: 12 }}
                    formatter={(value) => [fmt(Number(value)), 'Revenue']}
                  />
                  <Bar dataKey="total_revenue" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

      </div>

      {/* Metrics row: Customer + Order */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Customer metrics */}
        <div className={`rounded-xl border p-5 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <Users size={18} className="text-purple-500" />
            Customer Metrics
          </h2>
          {customerMetrics ? (
            <div className="space-y-4">
              <MetricRow icon={Users} label="Total Registered" value={String(customerMetrics.total_customers)} color="text-blue-500" isDark={isDark} />
              <MetricRow icon={UserPlus} label="New Customers during period" value={String(customerMetrics.new_customers)} color="text-emerald-500" isDark={isDark} />
              <MetricRow icon={RotateCcw} label="Returning Customers" value={String(customerMetrics.returning_customers)} color="text-purple-500" isDark={isDark} />
              <MetricRow icon={UserCheck} label="Return Rate" value={`${customerMetrics.return_rate.toFixed(1)}%`} color="text-amber-500" isDark={isDark} />

              {/* Return rate bar */}
              <div className="pt-2">
                <div className={`h-3 rounded-full overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <div
                    className="h-full bg-gradient-to-r from-purple-500 to-emerald-500 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(customerMetrics.return_rate, 100)}%` }}
                  />
                </div>
                <p className={`text-xs mt-1 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Customer retention rate
                </p>
              </div>
            </div>
          ) : (
            <p className={`text-sm text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No data yet</p>
          )}
        </div>

        {/* Order metrics */}
        <div className={`rounded-xl border p-5 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <ShoppingBag size={18} className="text-blue-500" />
            Order Metrics
          </h2>
          {orderMetrics ? (
            <div className="space-y-4">
              <MetricRow icon={Package} label="Total Orders" value={String(orderMetrics.total_orders)} color="text-blue-500" isDark={isDark} />
              <MetricRow icon={TrendingUp} label="New Orders during period" value={String(orderMetrics.new_orders)} color="text-emerald-500" isDark={isDark} />
              <MetricRow icon={RotateCcw} label="Returning Orders" value={String(orderMetrics.returning_orders)} color="text-purple-500" isDark={isDark} />
              <MetricRow icon={Repeat} label="Repeat Rate" value={`${orderMetrics.repeat_rate.toFixed(1)}%`} color="text-amber-500" isDark={isDark} />

              {/* Repeat rate bar */}
              <div className="pt-2">
                <div className={`h-3 rounded-full overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(orderMetrics.repeat_rate, 100)}%` }}
                  />
                </div>
                <p className={`text-xs mt-1 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Order repeat rate
                </p>
              </div>
            </div>
          ) : (
            <p className={`text-sm text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No data yet</p>
          )}
        </div>
      </div>
    </div>
  );
}

function MetricRow({ icon: Icon, label, value, color, isDark }: {
  icon: React.ElementType; label: string; value: string; color: string; isDark: boolean;
}) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
      <Icon size={18} className={color} />
      <span className={`flex-1 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{label}</span>
      <span className="font-bold text-sm">{value}</span>
    </div>
  );
}
